import { ref, computed } from 'vue'
import { useApi } from './useApi'
import type { Category } from '../types/common'

// Global, shared categories cache across the app
const categoriesRef = ref<Category[]>([])
const parentCategoriesRef = ref<Category[]>([])
let inflightCategoriesPromise: Promise<Category[]> | null = null
let inflightParentPromise: Promise<Category[]> | null = null

export function useCategories() {
  const { fetchData } = useApi()

  const normalizeCategory = (c: Partial<Category>): Category => {
    const id = c.id || c.slug || String(Math.random())
    // Handle both null and empty string as "no parent"
    const parent_id = c.parent_id === null || c.parent_id === "" ? undefined : c.parent_id
    const slug = c.slug || c.name?.toLowerCase?.().replace(/\s+/g, '-') || id
    const name = c.name || 'Unnamed'
    return {
      ...(c as object),
      id,
      name,
      slug,
      parent_id,
    } as Category
  }

  const loadCategories = async () => {
    // Return cached data if available
    if (categoriesRef.value.length > 0) {
      return categoriesRef.value
    }

    // Return existing promise if already in flight
    if (inflightCategoriesPromise) {
      return inflightCategoriesPromise
    }

    // Create new promise and store it
    inflightCategoriesPromise = (async () => {
      try {
        const data = await fetchData<Category[] | { categories?: Category[] }>(
          '/api/categories',
          false, // Enable caching to prevent rate limiting
        )
        
        // Data fetched successfully
        const arr = Array.isArray(data)
          ? data
          : data && typeof data === 'object' && Array.isArray((data as { categories?: Category[] }).categories)
          ? (data as { categories: Category[] }).categories
          : []
        categoriesRef.value = (arr as Partial<Category>[]).map(normalizeCategory)

        return categoriesRef.value
      } finally {
        // Clear the promise when done
        inflightCategoriesPromise = null
      }
    })()

    return inflightCategoriesPromise
  }

  const loadParentCategories = async () => {
    // Return cached data if available
    if (parentCategoriesRef.value.length > 0) {
      return parentCategoriesRef.value
    }

    // Return existing promise if already in flight
    if (inflightParentPromise) {
      return inflightParentPromise
    }

    // Create new promise and store it
    inflightParentPromise = (async () => {
      try {
        const data = await fetchData<Category[] | { categories?: Category[] }>(
          '/api/categories?parents_only=true',
          false, // Enable caching to prevent rate limiting
        )
        
        const arr = Array.isArray(data)
          ? data
          : data && typeof data === 'object' && Array.isArray((data as { categories?: Category[] }).categories)
          ? (data as { categories: Category[] }).categories
          : []
        parentCategoriesRef.value = (arr as Partial<Category>[]).map(normalizeCategory)

        return parentCategoriesRef.value
      } finally {
        // Clear the promise when done
        inflightParentPromise = null
      }
    })()

    return inflightParentPromise
  }

  const parentCategories = computed(() => parentCategoriesRef.value)

  const getSubcategoriesOf = (parent_id: string) =>
    categoriesRef.value.filter((c: Category) => c.parent_id === parent_id)

  const findCategory = (id: string) => 
    categoriesRef.value.find((cat: Category) => cat.id === id)

  const getSiblingCategories = (parent_id: string, excludeId: string) =>
    categoriesRef.value.filter((cat: Category) => cat.parent_id === parent_id && cat.id !== excludeId)

  const getPopularCategories = (excludeId: string, limit: number = 3) =>
    categoriesRef.value
      .filter((cat: Category) => cat.product_count > 0 && cat.id !== excludeId)
      .slice(0, limit)

  const isEmpty = () => categoriesRef.value.length === 0

  return {
    categories: categoriesRef,
    parentCategories,
    loadCategories,
    loadParentCategories,
    getSubcategoriesOf,
    findCategory,
    getSiblingCategories,
    getPopularCategories,
    isEmpty,
  }
}
